if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.currentMediaIndex = 0;
        this.previousButton = this.querySelector('[name="previous"]');
        this.nextButton = this.querySelector('[name="next"]');
        this.currentMediaCounter = this.querySelector('[data-current-media]');
        this.totalMediaCounter = this.querySelector('[data-total-media]');
        this.content = this.querySelector('.product-media-modal__content');

        this.previousButton?.addEventListener('click', () => this.changeMedia(-1));
        this.nextButton?.addEventListener('click', () => this.changeMedia(1));
        this.addEventListener('keydown', this.onKeydown.bind(this));

        [this.previousButton, this.nextButton, this.content].forEach((element) => {
          element?.addEventListener('pointerup', (event) => event.stopPropagation());
        });

        this.content?.addEventListener('click', this.onMediaClick.bind(this));
        this.content?.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.content?.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.content?.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.content?.addEventListener('pointercancel', this.onPointerUp.bind(this));
        this.content?.addEventListener('touchstart', this.onTouchStart.bind(this), { passive: true });
        this.content?.addEventListener('touchend', this.onTouchEnd.bind(this), { passive: true });
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        const requestedMediaId = this.openedBy.getAttribute('data-media-id');
        const requestedIndex = this.mediaItems.findIndex((media) => media.getAttribute('data-media-id') === requestedMediaId);
        this.currentMediaIndex = requestedIndex >= 0 ? requestedIndex : 0;
        this.renderActiveMedia();
      }

      get mediaItems() {
        return Array.from(this.content?.children || []).filter((element) => element.hasAttribute('data-media-id'));
      }

      renderActiveMedia() {
        const mediaItems = this.mediaItems;
        if (!mediaItems.length) return;

        this.resetZoom();
        this.currentMediaIndex = (this.currentMediaIndex + mediaItems.length) % mediaItems.length;

        mediaItems.forEach((media, index) => {
          const isActive = index === this.currentMediaIndex;
          media.classList.toggle('active', isActive);
          media.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        const activeMedia = mediaItems[this.currentMediaIndex];
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();

        if (this.currentMediaCounter) this.currentMediaCounter.textContent = this.currentMediaIndex + 1;
        if (this.totalMediaCounter) this.totalMediaCounter.textContent = mediaItems.length;

        const hasMultipleMedia = mediaItems.length > 1;
        if (this.previousButton) this.previousButton.hidden = !hasMultipleMedia;
        if (this.nextButton) this.nextButton.hidden = !hasMultipleMedia;
        const counter = this.querySelector('.product-media-modal__counter');
        if (counter) counter.hidden = !hasMultipleMedia;
      }

      changeMedia(direction) {
        this.resetZoom();
        this.currentMediaIndex += direction;
        this.renderActiveMedia();
      }

      onKeydown(event) {
        const keyCode = event.code?.toUpperCase();

        if (keyCode === 'ARROWLEFT') {
          event.preventDefault();
          this.changeMedia(-1);
        }

        if (keyCode === 'ARROWRIGHT') {
          event.preventDefault();
          this.changeMedia(1);
        }

        if (keyCode === 'EQUAL' || keyCode === 'NUMPADADD') {
          event.preventDefault();
          this.setZoom(true);
        }

        if (keyCode === 'MINUS' || keyCode === 'NUMPADSUBTRACT') {
          event.preventDefault();
          this.setZoom(false);
        }
      }

      get activeMedia() {
        return this.mediaItems[this.currentMediaIndex];
      }

      get isZoomed() {
        return this.classList.contains('is-zoomed');
      }

      toggleZoom() {
        this.setZoom(!this.isZoomed);
      }

      setZoom(zoomed) {
        const activeMedia = this.activeMedia;
        if (!activeMedia || activeMedia.nodeName !== 'IMG') return;

        if (!zoomed) {
          this.resetZoom();
          return;
        }

        const fittedWidth = activeMedia.getBoundingClientRect().width;
        activeMedia.style.width = `${Math.max(fittedWidth * 2, activeMedia.naturalWidth || 0)}px`;
        activeMedia.style.maxWidth = 'none';
        activeMedia.style.maxHeight = 'none';
        this.classList.add('is-zoomed');

        requestAnimationFrame(() => {
          this.content.scrollLeft = (this.content.scrollWidth - this.content.clientWidth) / 2;
          this.content.scrollTop = (this.content.scrollHeight - this.content.clientHeight) / 2;
        });
      }

      resetZoom() {
        const activeMedia = this.activeMedia;
        if (activeMedia?.nodeName === 'IMG') {
          activeMedia.style.removeProperty('width');
          activeMedia.style.removeProperty('max-width');
          activeMedia.style.removeProperty('max-height');
        }

        this.classList.remove('is-zoomed', 'is-panning');
        this.isPanning = false;
        this.hasDragged = false;
        if (this.content) {
          this.content.scrollLeft = 0;
          this.content.scrollTop = 0;
        }
      }

      onMediaClick(event) {
        if (event.target !== this.activeMedia) return;
        if (this.hasDragged) {
          this.hasDragged = false;
          return;
        }
        this.toggleZoom();
      }

      onPointerDown(event) {
        if (!this.isZoomed || event.target !== this.activeMedia || event.pointerType === 'touch') return;
        this.isPanning = true;
        this.hasDragged = false;
        this.pointerStartX = event.clientX;
        this.pointerStartY = event.clientY;
        this.scrollStartLeft = this.content.scrollLeft;
        this.scrollStartTop = this.content.scrollTop;
        this.classList.add('is-panning');
      }

      onPointerMove(event) {
        if (!this.isPanning) return;
        const deltaX = event.clientX - this.pointerStartX;
        const deltaY = event.clientY - this.pointerStartY;
        if (Math.abs(deltaX) > 4 || Math.abs(deltaY) > 4) this.hasDragged = true;
        this.content.scrollLeft = this.scrollStartLeft - deltaX;
        this.content.scrollTop = this.scrollStartTop - deltaY;
      }

      onPointerUp(event) {
        if (!this.isPanning) return;
        this.isPanning = false;
        this.classList.remove('is-panning');
      }

      onTouchStart(event) {
        this.touchStartX = event.changedTouches[0]?.clientX;
      }

      onTouchEnd(event) {
        if (this.isZoomed) return;
        if (this.touchStartX == null) return;
        const touchEndX = event.changedTouches[0]?.clientX;
        const distance = touchEndX - this.touchStartX;
        this.touchStartX = null;

        if (Math.abs(distance) < 50) return;
        this.changeMedia(distance > 0 ? -1 : 1);
      }
    }
  );
}
